import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateOrganizationSchema, generateLocalBusinessSchema } from '../../utils/seo';

const HomePage = () => {
  // SEO Configuration
  useSEO({
    title: 'PURECODE Ltd - Premier IT Consultancy Warrington Banking Fintech Solutions',
    description: 'PURECODE Ltd is Warrington\'s leading IT consultancy specialising in AI-driven solutions for banking, fintech, and betting industries. Expert software development, cloud infrastructure, and cybersecurity services. FCA authorised, ISO 27001 certified.',
    keywords: 'IT consultancy Warrington, banking technology, fintech solutions, AI development, machine learning, financial software UK, Warrington tech company',
    canonical: 'https://purecode.uk/',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateLocalBusinessSchema(),
        {
          '@type': 'WebSite',
          '@id': 'https://purecode.uk/#website',
          url: 'https://purecode.uk/',
          name: 'PURECODE Ltd',
          description: 'Premier IT consultancy for banking, fintech, and betting industries',
          publisher: {
            '@id': 'https://purecode.uk/#organization'
          },
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://purecode.uk/?s={search_term_string}',
            'query-input': 'required name=search_term_string'
          }
        }
      ]
    }
  });

  const services = [
    {
      icon: 'ri-code-s-slash-line',
      title: 'Custom Software Development',
      description: 'Tailored <strong>software solutions</strong> for small to medium <strong>financial services</strong>, <strong>fintech startups</strong>, and local <strong>betting platforms</strong>.'
    },
    {
      icon: 'ri-brain-line',
      title: 'AI & Machine Learning',
      description: 'Smart <strong>AI solutions</strong> for fraud detection, customer analytics, and automated systems for growing businesses.'
    },
    {
      icon: 'ri-cloud-line',
      title: 'Cloud Infrastructure',
      description: 'Secure, scalable <strong>cloud solutions</strong> with 99.5% uptime for <strong>financial services</strong> and emerging <strong>fintech companies</strong>.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Cybersecurity & Compliance',
      description: '<strong>Security frameworks</strong>, compliance assistance, and risk management systems for regulated businesses.'
    },
    {
      icon: 'ri-database-2-line',
      title: 'Data Analytics & BI',
      description: 'Analytics platforms for business insights, customer behaviour analysis, and operational reporting.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Digital Transformation',
      description: '<strong>Digital transformation</strong> consulting for traditional businesses moving into the <strong>fintech space</strong>.'
    }
  ];

  const stats = [
    { number: '£2M+', label: 'Transaction Volume Processed' },
    { number: '15+', label: 'Clients Served' },
    { number: '99.5%', label: 'System Uptime Achieved' },
    { number: '8+', label: 'Years Industry Experience' }
  ];

  const testimonials = [
    {
      name: 'Michael Thompson',
      company: 'FinanceFirst Solutions',
      role: 'Managing Director',
      content: 'PURECODE delivered an excellent customer management system that streamlined our operations. Their attention to detail and understanding of our business needs was impressive.',
      avatar: 'https://public.readdy.ai/ai/img_res/54e47474ef93e4372ff25497c1f36ef9.jpg'
    },
    {
      name: 'Emma Roberts',
      company: 'LocalBet Gaming',
      role: 'Operations Manager',
      content: 'The analytics platform PURECODE built helps us track our betting operations efficiently. Their team was responsive and delivered exactly what we needed within budget.',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20businesswoman%20with%20brown%20hair%20in%20elegant%20blazer%2C%20warm%20smile%2C%20modern%20small%20office%20environment%2C%20executive%20portrait%20style%2C%20natural%20lighting&width=80&height=80&seq=testimonial2&orientation=squarish'
    },
    {
      name: 'James Wilson',
      company: 'TechCredit Ltd',
      role: 'Founder & CEO',
      content: 'PURECODE helped us build our lending platform from scratch. Their expertise in financial technology and regulatory requirements was exactly what our startup needed.',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20startup%20founder%20in%20his%2030s%20wearing%20casual%20blazer%2C%20confident%20demeanor%2C%20modern%20co-working%20space%20background%2C%20entrepreneur%20headshot%20style%2C%20professional%20lighting&width=80&height=80&seq=testimonial3&orientation=squarish'
    }
  ];

  const achievements = [
    {
      icon: 'ri-award-line',
      title: 'Industry Certified',
      description: 'Certified to work with financial service providers'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security Focused',
      description: 'Following best practices for information security'
    },
    {
      icon: 'ri-medal-line',
      title: 'Cloud Expertise',
      description: 'Experienced in modern cloud and AI solutions'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Proven Results',
      description: 'Successful track record in fintech deployments'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header isHomePage={true} />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 text-white min-h-screen flex items-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.85), rgba(8, 145, 178, 0.85)), url('https://readdy.ai/api/search-image?query=Small%20modern%20tech%20office%20with%205%20developers%20working%20on%20computers%2C%20collaborative%20workspace%2C%20startup%20environment%2C%20financial%20technology%20development%2C%20warm%20lighting%20with%20orange%20tones&width=1920&height=1080&seq=hero1&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 via-cyan-900/70 to-transparent"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-teal-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-3xl">
              <div className="mb-6">
                <span className="inline-flex items-center rounded-full bg-teal-500/20 px-4 py-2 text-sm font-medium text-teal-200 ring-1 ring-teal-500/30">
                  <i className="ri-award-line mr-2"></i>
                  Specialized IT Consultancy • Small Team, Big Impact
                </span>
              </div>
              
              <h2 className="text-5xl font-bold tracking-tight sm:text-7xl mb-6 leading-tight">
                Specialized
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-300">
                  Fintech Solutions
                </span>
              </h2>
              
              <p className="text-xl leading-8 text-teal-100 mb-8">
                <strong>PURECODE Ltd</strong> is a boutique <strong>IT consultancy</strong> specializing in custom <strong>software solutions</strong> for 
                <strong> fintech startups</strong>, small <strong>financial services</strong>, and emerging <strong>betting platforms</strong>. We deliver 
                personalized service with enterprise-quality results in <strong>Warrington</strong> and across the UK.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link
                  to="/contact"
                  className="rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 px-8 py-4 text-lg font-semibold text-white shadow-xl hover:from-teal-400 hover:to-cyan-400 transform hover:scale-105 transition-all duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  Start Your Project
                </Link>
                <Link
                  to="/projects"
                  className="rounded-lg border-2 border-teal-300 px-8 py-4 text-lg font-semibold text-teal-200 hover:bg-teal-500/20 hover:border-teal-200 transition-all duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  View Our Work
                </Link>
              </div>
              
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-teal-300">£2M+</div>
                  <div className="text-sm text-teal-200">Transactions Handled</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-cyan-300">99.5%</div>
                  <div className="text-sm text-teal-200">System Uptime</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-300">15+</div>
                  <div className="text-sm text-teal-200">Happy Clients</div>
                </div>
              </div>
            </div>
            
            <div className="relative lg:block hidden">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Small%20team%20of%20developers%20working%20on%20financial%20software%2C%20modern%20startup%20office%20environment%2C%20collaborative%20coding%20session%2C%20fintech%20development%20workspace%20with%20multiple%20monitors&width=600&height=700&seq=hero2&orientation=portrait"
                  alt="PURECODE Development Team - IT Consultancy Warrington"
                  className="rounded-2xl shadow-2xl object-cover w-full h-96 lg:h-[500px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/50 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-16 bg-gradient-to-r from-teal-50 to-cyan-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full shadow-lg mx-auto mb-4">
                  <i className={`${achievement.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{achievement.title}</h3>
                <p className="text-sm text-gray-600">{achievement.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <h3 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
              Boutique Financial Technology Services
            </h3>
            <p className="text-xl leading-8 text-gray-600">
              Personalized <strong>IT services</strong> for <strong>fintech startups</strong>, small <strong>financial services</strong>, 
              and growing businesses in the financial sector
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <article key={index} className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-teal-200">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${service.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">{service.title}</h4>
                <p className="text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: service.description }}></p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Trusted by Growing Businesses</h2>
            <p className="text-xl text-teal-100">Quality results from our dedicated team</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="text-white">
                <div className="text-5xl font-bold mb-3 bg-gradient-to-r from-cyan-300 to-teal-300 bg-clip-text text-transparent">{stat.number}</div>
                <div className="text-teal-100 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
                Why Growing Businesses Choose PURECODE
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                As a boutique consultancy with 8+ years of experience, we provide personalized attention 
                and enterprise-quality solutions that help businesses scale efficiently.
              </p>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full mr-4 mt-1">
                    <i className="ri-check-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Personalized Service</h4>
                    <p className="text-gray-600">Direct access to senior developers and consultants</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full mr-4 mt-1">
                    <i className="ri-check-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Cost-Effective Solutions</h4>
                    <p className="text-gray-600">Enterprise quality without enterprise overhead</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full mr-4 mt-1">
                    <i className="ri-check-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Proven Experience</h4>
                    <p className="text-gray-600">Successfully delivered 50+ projects for growing businesses</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full mr-4 mt-1">
                    <i className="ri-check-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Modern Technology</h4>
                    <p className="text-gray-600">Latest tools and frameworks for competitive advantage</p>
                  </div>
                </div>
              </div>
              <Link
                to="/about"
                className="inline-block mt-8 rounded-lg bg-teal-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:bg-teal-500 transform hover:scale-105 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Learn About Our Team
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Small%20fintech%20development%20team%20of%205%20people%20in%20modern%20office%2C%20diverse%20professionals%20collaborating%20on%20software%20projects%2C%20startup%20workspace%20with%20multiple%20monitors%20and%20whiteboards&width=600&height=500&seq=about1&orientation=landscape"
                alt="PURECODE development team"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="text-2xl font-bold text-teal-600">8+</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
              Trusted by Growing Businesses
            </h2>
            <p className="text-xl leading-8 text-gray-600">
              Hear from business owners who've grown their operations with PURECODE's solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-teal-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4 ring-4 ring-white shadow-lg"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{testimonial.name}</h4>
                    <p className="text-sm text-teal-600 font-medium">{testimonial.role}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic leading-relaxed">"{testimonial.content}"</p>
                <div className="flex mt-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20startup%20office%20environment%20with%20team%20collaboration%2C%20fintech%20development%20workspace%2C%20small%20business%20technology%20solutions%2C%20warm%20lighting%20with%20orange%20tones&width=1920&height=600&seq=cta1&orientation=landscape"
            alt="PURECODE Office"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-6">
            Ready to Scale Your Business?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
            Join growing businesses who trust PURECODE to deliver quality fintech solutions. 
            Let's discuss how our boutique team can help accelerate your digital transformation.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/contact"
              className="rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 px-8 py-4 text-lg font-semibold text-white shadow-xl hover:from-teal-400 hover:to-cyan-400 transform hover:scale-105 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Get Free Consultation
            </Link>
            <Link
              to="/projects"
              className="rounded-lg border-2 border-teal-300 px-8 py-4 text-lg font-semibold text-white hover:bg-teal-500/20 hover:border-teal-200 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              See Our Work
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
